import type { AdminNavItem, AdminPermission, AdminRole } from '../types/admin';

export const ADMIN_ROLES: AdminRole[] = ['super_admin', 'admin', 'cms', 'editor'];

export function isAdminRole(role?: string | null): role is AdminRole {
  return Boolean(role && ADMIN_ROLES.includes(role as AdminRole));
}

export function isSuperAdmin(role?: string | null) {
  return role === 'super_admin';
}

export function canAccessAdmin(role?: string | null) {
  return isAdminRole(role);
}

/** Staff roles that must not use the public student site. */
export function usesAdminConsoleOnly(role?: string | null) {
  return role === 'super_admin' || role === 'admin' || role === 'cms';
}

export function hasAdminPermission(
  permissions: AdminPermission[] | undefined,
  permission: AdminPermission,
) {
  return Boolean(permissions?.includes(permission));
}

export const ADMIN_NAV: AdminNavItem[] = [
  { id: 'dashboard', label: 'Dashboard', route: '/admin', icon: 'LayoutDashboard', permission: 'dashboard.view' },
  {
    id: 'users',
    label: 'User Management',
    icon: 'Users',
    permission: 'users.manage',
    children: [
      { id: 'users-list', label: 'Users', route: '/admin/users', icon: 'Users', permission: 'users.manage' },
      { id: 'roles', label: 'Roles', route: '/admin/roles', icon: 'Shield', permission: 'roles.manage', superAdminOnly: true },
      { id: 'permissions', label: 'Permissions', route: '/admin/permissions', icon: 'KeyRound', permission: 'roles.manage', superAdminOnly: true },
    ],
  },
  {
    id: 'content',
    label: 'Content Management',
    icon: 'FileText',
    permission: 'content.manage',
    children: [
      { id: 'pages', label: 'Pages', route: '/admin/content/pages', icon: 'Files', permission: 'content.manage' },
      { id: 'blogs', label: 'Blogs', route: '/admin/content/blogs', icon: 'Newspaper', permission: 'content.manage' },
      { id: 'faqs', label: 'FAQs', route: '/admin/content/faqs', icon: 'CircleHelp', permission: 'content.manage' },
      { id: 'announcements', label: 'Announcements', route: '/admin/content/announcements', icon: 'Megaphone', permission: 'content.manage' },
      { id: 'navigation', label: 'Navigation', route: '/admin/content/navigation', icon: 'Compass', permission: 'content.manage' },
      { id: 'banners', label: 'Banners', route: '/admin/content/banners', icon: 'Image', permission: 'content.manage' },
    ],
  },
  { id: 'results', label: 'Results', route: '/admin/results', icon: 'ClipboardList', permission: 'results.manage' },
  { id: 'opportunities', label: 'Opportunities', route: '/admin/opportunities', icon: 'Briefcase', permission: 'opportunities.manage' },
  { id: 'academy', label: 'Academy', route: '/admin/academy', icon: 'GraduationCap', permission: 'content.manage' },
  { id: 'jobs', label: 'Jobs', route: '/admin/jobs', icon: 'Building2', permission: 'opportunities.manage' },
  { id: 'colleges', label: 'Colleges', route: '/admin/colleges', icon: 'School', permission: 'content.manage' },
  { id: 'media', label: 'Media Library', route: '/admin/media', icon: 'Images', permission: 'media.manage' },
  { id: 'notifications', label: 'Notifications', route: '/admin/notifications', icon: 'Bell', permission: 'notifications.manage' },
  { id: 'seo', label: 'SEO Manager', route: '/admin/seo', icon: 'Search', permission: 'seo.manage' },
  { id: 'analytics', label: 'Analytics', route: '/admin/analytics', icon: 'BarChart3', permission: 'analytics.view' },
  { id: 'support', label: 'Support Tickets', route: '/admin/support', icon: 'LifeBuoy', permission: 'support.manage' },
  { id: 'audit', label: 'Audit Logs', route: '/admin/audit', icon: 'ScrollText', permission: 'audit.view' },
  { id: 'ai', label: 'AI Assistant', route: '/admin/ai', icon: 'Sparkles', permission: 'ai.assistant' },
  { id: 'settings', label: 'Settings', route: '/admin/settings', icon: 'Settings', permission: 'settings.manage' },
  { id: 'profile', label: 'Profile', route: '/admin/profile', icon: 'UserCircle', permission: 'dashboard.view' },
];

export function filterAdminNav(
  items: AdminNavItem[],
  permissions: AdminPermission[],
  isSuperAdminUser: boolean,
): AdminNavItem[] {
  return items
    .filter((item) => {
      if (item.superAdminOnly && !isSuperAdminUser) return false;
      if (item.permission && !permissions.includes(item.permission)) return false;
      return true;
    })
    .map((item) => ({
      ...item,
      children: item.children
        ? filterAdminNav(item.children, permissions, isSuperAdminUser)
        : undefined,
    }))
    .filter((item) => !item.children || item.children.length > 0);
}
